/**
 * `BmsPrincipal`, the one value both lanes produce and every BMS service takes
 * as its first argument (the wiring plan §4).
 *
 * BROWSER-SAFE. The internal middleware Tag declares this as what it
 * `provides`, and a contract module binds that Tag, so this module is in the
 * browser's import graph. It imports only `effect`, its own domain siblings and
 * three type-only paths.
 * The public-api import is RELATIVE for the same reason `calls-internal-api.ts`
 * gives: the public-api barrel re-exports repositories that reach
 * `@effect/sql-pg`, and a Postgres driver must not land in the client bundle.
 *
 * ONE VALUE, TWO LANES. A signed-in broker and a firm's API key arrive by
 * completely different chains and become the same shape, so a service can
 * never be written to work on one lane and quietly not the other, and the
 * later agent tools are a third adapter over the same call.
 *
 * SCOPES ON THE SESSION LANE. A signed-in broker is NOT scope-limited: the
 * limits that apply to them are the organisation filter and the client list
 * (`../infrastructure/scope.ts`), not a credential's grant. So a session
 * principal carries the full scope set and `requireBmsScope` is a no-op for
 * it. The public lane is where scopes bite.
 */
import { Context, Effect, Schema as S } from "effect"

import type { AuditActorShape } from "@/contexts/audit/ledger"
import type { CovaUserId, OrgId } from "@/domain/shared/branded-types"
// RELATIVE, not the public-api barrel. See the header.
import type { CredentialId, IntegrationId } from "../../../public-api/domain/api-principal"
import { BmsForbiddenError, BmsRegionUnsetError } from "./errors"
import { mayDo, type PermissionActor, refusalOf, type RefusableFirmPermissionKey } from "./firm-permissions"

/**
 * Re-exported so a leaf never imports the presentation layer to ask whether a
 * region is configured, and never writes the bare `"unset"` literal.
 */
export { SESSION_REGION_UNSET } from "@/presentation/http/shared/session-principal"
import { SESSION_REGION_UNSET } from "@/presentation/http/shared/session-principal"

/**
 * A signed-in broker. `covaUserId` is nullable for the same reason
 * `SessionPrincipal`'s is: `org_members.user_id` is not yet reconciled for
 * every row, and a write that FKs `users(id)` must guard rather than assert.
 */
export interface BmsUserActor {
  readonly kind: "user"
  /** The Clerk id. What `audit_events.actor_id` records. */
  readonly userId: string
  readonly covaUserId: CovaUserId | null
  readonly isAdmin: boolean
  /**
   * The firm's own administrator, as distinct from `isAdmin`, which also
   * covers a super-admin acting inside the firm. Optional because test
   * literals predate it; both principal builders set it. The cutover's
   * firm-admin gates read this and never `isAdmin` (cutover plan ruling 1,
   * the agreement is the firm's own administrator's).
   */
  readonly isOrgAdmin?: boolean
}

/** A firm's API credential. No `isAdmin`, no team, no client grants. */
export interface BmsIntegrationActor {
  readonly kind: "integration"
  readonly integrationId: IntegrationId
  readonly credentialId: CredentialId
}

/**
 * A job acting on its own account, with no person and no firm key behind it:
 * the renewal sweep's `cron:renewal-sweep` (plan ruling 24). It is nobody's
 * user and nobody's integration, so every branch on `actor.kind` says what a
 * job gets rather than borrowing a sentinel id for one of the other two.
 */
export interface BmsSystemActor {
  readonly kind: "system"
  /** The job's reserved name, `cron:<name>`. */
  readonly job: string
}

export type BmsActor = BmsUserActor | BmsIntegrationActor | BmsSystemActor

/**
 * WHY a write under this principal happened, when the answer is not "a broker
 * typed it" (research 08 §5.3).
 *
 * STRUCTURAL, AND NOT THE AUDIT LEAF'S `AuditCause`, which is what it mirrors
 * field for field. A type-only import would erase at runtime and keep this
 * module browser-safe, but `shared/` naming `settings/audit/domain/` is a deep
 * import into a leaf and the context-group ratchet refuses it for everyone but
 * that leaf. So the shape is written out here and the audit leaf NARROWS it:
 * `causeFrom` in `audit-input.ts` is the one place a `string` becomes an
 * `AuditCauseKind`, and `causeColumnsFor` beside it is what the ledger's two
 * write sites call. An unrecognised word is dropped rather than written,
 * because `chk_ae_cause_kind` would refuse the insert and take the caller's
 * write down with it.
 *
 * ABSENT ON EVERY BROWSER CALL, which is the whole point. The session
 * middleware builds no cause, so all 88 `cause: { kind: "direct" }` literals
 * the book's services already write stay exactly as they are and every one of
 * them keeps meaning what it says. A `cova` command sets it, and the ledger
 * prefers it only where the writer said `direct`, so a revert still records
 * `revert` and a workflow still records `workflow`.
 */
export interface BmsPrincipalCause {
  /** One of `audit_events`' cause kinds. `agent_run` is the CLI's. */
  readonly kind: string
  readonly id?: string | null
  readonly label?: string | null
}

export interface BmsPrincipal {
  readonly organizationId: OrgId
  /** The org's region, or `SESSION_REGION_UNSET`. Never a guessed country. */
  readonly region: string
  /**
   * The firm's own IANA zone (`organisations.time_zone`), when the door that
   * built the principal read one. Absent or null means "not chosen": read the
   * firm's day through `firmTimeZone(principal.timeZone, principal.region)`,
   * which falls back to the region profile's default zone, then UTC.
   */
  readonly timeZone?: string | null
  readonly actor: BmsActor
  readonly scopes: ReadonlySet<string>
  /**
   * Why this principal's writes happened, when a door other than a browser
   * opened them. Absent on both shipped lanes; see `BmsPrincipalCause`.
   *
   * It sits beside the actor because that is where a reader looks for it: the
   * ledger already derives the actor half of the same row from this value, and
   * a cause arriving from a FiberRef would be a value with no visible source.
   */
  readonly cause?: BmsPrincipalCause

  /**
   * The firm's written instruction a staff write acts on (Tai, 2026-11-03):
   * who at the firm gave it and where it is. Set only by a staff door that
   * writes on a firm's instruction (the instructed archive); the ledger puts
   * it on every event under this principal as `detail.onBehalf`. It names a
   * person at the firm, never the staff member, who is on the staff trail.
   */
  readonly onBehalf?: { readonly instructedBy: string; readonly reference: string }

  /**
   * Who the platform ledger records for every write this principal makes
   * (spec §5.3, ruling 13). Bound ONCE, at the door that builds the principal,
   * with that door's binder: `userActorFrom` on the session lane and the files
   * door, `integrationActor` on the firm-key lane, `systemActor` for a cron.
   * `AuditLedger.record` reads it and nothing else decides it, so a write
   * cannot be recorded under an actor its door did not authenticate.
   */
  readonly auditActor: AuditActorShape

  /**
   * Present only when a Cova staff member is looking at this firm through the
   * CRM viewer (spec ruling S2). The actor above is then the viewed member, or
   * a synthetic administrator over the staff person's Clerk id, so every
   * service reads the firm exactly as that member would. This field is how the
   * lane knows a staff person is behind it: who they are, whose view they
   * chose, and the one door their read audit goes through. Absent on every
   * broker, key and job principal.
   */
  readonly staffView?: BmsStaffView
}

/** What the viewer's read audit may carry: names and ids only (R-12). */
export type StaffReadDetail = Readonly<
  Record<string, string | number | boolean | null | ReadonlyArray<string>>
>

/**
 * The staff person behind a view-as principal (spec R-02). Not a fourth actor
 * kind (spec §11): the actor stays the viewed member so no `actor.kind` branch
 * in the book has to learn about staff.
 */
export interface BmsStaffView {
  readonly staffUserId: string
  readonly staffCovaUserId: CovaUserId | null
  readonly staffOrganizationId: OrgId
  readonly staffAuditRef: string
  /** The See as member's Clerk id; null is the administrator's view. */
  readonly seeAsUserId: string | null
  /**
   * The staff row and the firm's `viewed` row, one transaction; dies on
   * failure. The failure channel is `never` on purpose: the recorder logs and
   * dies on a failed append, so the response is the boundary's defect path
   * and no contract gains an error class (spec §11).
   */
  readonly recordRead: (operation: string, detail: StaffReadDetail) => Effect.Effect<void>
  /**
   * OR-01's backstop (research 09 §2.6): runs the handler's effect inside a
   * transaction whose FIRST statement is `SET TRANSACTION READ ONLY`, so a
   * write the class table wrongly called a read fails 25006 and lands nothing,
   * and under `holdSideEffects`, so a write it would fork through
   * `fireAndForget` (which leaves the transaction) is never forked. A write
   * made outside the transaction any other way is the class table's to refuse.
   * Transaction-local, never a session setting, so the next transaction on the
   * pooled connection is writable again. The handler tail runs `recordRead`
   * after it commits, in its own transaction, because the audit door is
   * refused inside a read-only one. Its own SQL failure is logged and dies.
   * Optional because test literals predate it; the staff branch always sets it.
   */
  readonly readOnly?: <A, E, R>(effect: Effect.Effect<A, E, R>) => Effect.Effect<A, E, R>
}

export class BmsPrincipalTag extends Context.Tag("Bms/BmsPrincipal")<
  BmsPrincipalTag,
  BmsPrincipal
>() {}

/**
 * The region, or a typed failure. The ONLY way a service reads a region.
 *
 * There is no overload that takes a fallback, on purpose. A tax rate, a
 * currency or a statutory list chosen because the firm never configured a
 * region is a wrong number that looks right, and the sentinel exists precisely
 * so that state is visible rather than plausible.
 */
export const requireRegion = (
  principal: BmsPrincipal,
): Effect.Effect<string, BmsRegionUnsetError> =>
  principal.region === SESSION_REGION_UNSET || principal.region.length === 0
    ? Effect.fail(
        new BmsRegionUnsetError({
          message:
            "This firm has no region configured, so this cannot be calculated. Set the region in Settings.",
        }),
      )
    : Effect.succeed(principal.region)

/** Is this principal a broker signed in through the internal lane? */
export const isUserActor = (principal: BmsPrincipal): principal is BmsPrincipal & {
  readonly actor: BmsUserActor
} => principal.actor.kind === "user"

/** An org admin on the session lane. An integration is NEVER an admin. */
export const isAdminUser = (principal: BmsPrincipal): boolean =>
  principal.actor.kind === "user" && principal.actor.isAdmin

/**
 * The actor as the firm's permissions read it (spec 2026-10-26-001 §2): a
 * person with the firm's admin flag (`isAdmin`, which covers Cova staff acting
 * inside the firm) and the firm's own admin flag (`isOrgAdmin`), a firm key, or
 * a job. Pass the principal's `scopes` when a firm key's write scope should
 * count (an entry's `keyScope`); `requirePermission` always does.
 */
export const permissionActorOf = (actor: BmsActor, scopes?: ReadonlySet<string>): PermissionActor => {
  switch (actor.kind) {
    case "user":
      return { kind: "person", admin: actor.isAdmin, firmAdmin: actor.isOrgAdmin === true }
    case "integration":
      return scopes === undefined ? { kind: "key" } : { kind: "key", scopes }
    case "system":
      return { kind: "job" }
  }
}

/**
 * THE ADMIN GATE, on every lane: the principal may do `key`, or it fails with
 * the entry's own sentence (`FIRM_PERMISSIONS` in `firm-permissions.ts`), so a
 * refusal on the wire reads as it did before the list existed.
 *
 * IN THE SERVICE, NEVER MIDDLEWARE (decision 048). `cova` and the agent call
 * services directly, and every group mixes open reads with gated writes. Ask it
 * first, before any read, so a refused caller learns nothing but the rule.
 */
export const requirePermission = (
  principal: BmsPrincipal,
  key: RefusableFirmPermissionKey,
): Effect.Effect<void, BmsForbiddenError> =>
  mayDo(permissionActorOf(principal.actor, principal.scopes), key)
    ? Effect.void
    : Effect.fail(new BmsForbiddenError({ message: refusalOf(key) }))

/**
 * What `withPermission` takes: one key, or several over one method whose body
 * then asks each key for the part it changes (the firm's preferences). Several
 * pass a caller who may do any one of them, and refuse in the first one's words.
 */
export type PermissionGate =
  | RefusableFirmPermissionKey
  | readonly [RefusableFirmPermissionKey, ...ReadonlyArray<RefusableFirmPermissionKey>]

type PermissionGuard = (principal: BmsPrincipal) => Effect.Effect<void, BmsForbiddenError>

const guardFor = (gate: PermissionGate): PermissionGuard => {
  if (typeof gate === "string") return (principal) => requirePermission(principal, gate)
  const [first] = gate
  return (principal) => {
    const actor = permissionActorOf(principal.actor, principal.scopes)
    return gate.some((key) => mayDo(actor, key))
      ? Effect.void
      : Effect.fail(new BmsForbiddenError({ message: refusalOf(first) }))
  }
}

/**
 * Each gated method's guard, keyed by the method itself, so a handler can ask
 * it before its receipt. Kept beside the method rather than on its type: a
 * gated method's type stays the plain function type, which is what an
 * `Effect.Service` turns into an accessor.
 */
const permissionGuards = new WeakMap<object, PermissionGuard>()

/**
 * A service method with `requirePermission` in front of it, for a service that
 * hands its methods out in one object: `update: withPermission("registers.tags",
 * update)`. The guard runs before the method is even built, so a refused caller
 * reaches none of the method's reads or writes (decision 048).
 *
 * A HANDLER THAT CLAIMS A RECEIPT OUTSIDE THE SERVICE asks the same guard first,
 * through `permissionGuardOf(service.update)(principal)`, so a refused caller
 * opens no transaction, reads no receipt and is never handed a stored answer.
 */
export const withPermission = <Args extends ReadonlyArray<unknown>, A, E, R>(
  gate: PermissionGate,
  method: (principal: BmsPrincipal, ...args: Args) => Effect.Effect<A, E, R>,
): ((principal: BmsPrincipal, ...args: Args) => Effect.Effect<A, E | BmsForbiddenError, R>) => {
  const guard = guardFor(gate)
  const wrapped = (principal: BmsPrincipal, ...args: Args): Effect.Effect<A, E | BmsForbiddenError, R> =>
    Effect.zipRight(
      guard(principal),
      Effect.suspend(() => method(principal, ...args)),
    )
  permissionGuards.set(wrapped, guard)
  return wrapped
}

/**
 * The guard a `withPermission` method carries, for a handler to run before its
 * own receipt. A method handed out without one answers `Effect.void`: it is
 * everyone's, or it holds its gate in its own body.
 */
export const permissionGuardOf =
  (method: object): PermissionGuard =>
  (principal) =>
    permissionGuards.get(method)?.(principal) ?? Effect.void

/**
 * The id a write's own actor columns record (`created_by_user_id`,
 * `workflow_runs.started_by_user_id` and the like), whichever door it came
 * through. A job records its reserved name: the columns are TEXT with no
 * foreign key, and the name is visibly the platform rather than a person.
 */
export const actorIdOf = (principal: BmsPrincipal): string => {
  switch (principal.actor.kind) {
    case "user":
      return principal.actor.userId
    case "integration":
      return principal.actor.integrationId
    case "system":
      return principal.actor.job
  }
}

/** The wire shape of a scope name. Not a closed union here: the kit is leaf-agnostic. */
export const BmsScopeName = S.String.pipe(S.pattern(/^bms:[a-z-]+:(read|write)$/))
